import { Injectable, signal, Injector } from '@angular/core';
import { HttpClient } from "@angular/common/http";
import { ToastrService, ActiveToast } from "ngx-toastr";
import { TranslateService } from "@ngx-translate/core";
import { catchError, map, throwError } from "rxjs";
import { ConfigService } from "./config.service";
import { ErrorMapperService } from "./error-mapper.service";
import { SystemMessage } from "../models/message.model";
import { ModalService } from "./modal.service";
import { AppErrorModel } from '../models/appError.model';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class MessageService {

  private currentMessageSignal = signal<SystemMessage | null>(null);
  currentMessage = this.currentMessageSignal.asReadonly();

  private hasUnreadSignal = signal<boolean>(false);
  hasUnread = this.hasUnreadSignal.asReadonly();

  private activeToast?: ActiveToast<any>;

  constructor(
    private http: HttpClient,
    private configs: ConfigService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private errorMapper: ErrorMapperService,
    private modalService: ModalService,
    private injector: Injector
  ) { }

  private get authService(): AuthService {
    return this.injector.get(AuthService); //this lazy loads authService, when needed
  }

  updateHasUnreadSignal(hasUnread: boolean): void {
    this.hasUnreadSignal.set(hasUnread);
    hasUnread ? this.showUnreadToastr() : this.clearToastr();
  }

  showUnreadToastr(): void {
    if (this.activeToast || this.currentMessageSignal()) return;

    const message = this.translate.instant('ACCOUNT.MESSAGES.UNREAD_NOTIFICATION_MESSAGE');
    const title = this.translate.instant('ACCOUNT.MESSAGES.UNREAD_NOTIFICATION_TITLE');

    this.activeToast = this.toastr.info(message, title, {
      toastClass: 'ngx-toastr lobbyless-toast clickable-toast',
      timeOut: 0,
      extendedTimeOut: 0,
      closeButton: false
    });

    this.activeToast.onTap.subscribe(() => {
      this.readNext();
    });
  }

  readNext(): void {
    this.http.post<any>(`${this.configs.apiUrl}`, { "jsonrpc": "2.0", "id": this.configs.casinoId, "method": "user.message.read", "params": {} }).pipe(
      map(response => {
        if ('result' in response && 'message' in response.result) {
          return response.result.message as SystemMessage | null;
          // return { id: 111, subject: 'test subject', body: 'test body' } //todo delete - this is just for testing
        }
        throw response;
      }),
      catchError(error => throwError(() => this.errorMapper.mapAPIerrors(error)))
    ).subscribe({
      next: (message) => {
        if (message) {
          this.currentMessageSignal.set(message);
          this.clearToastr();
        }
      },
      error: (err: AppErrorModel) => {
        console.error('Error reading message:', err)
        switch (err.type) {
          case "SESSION":
            this.authService.resetUserSession();
            this.toastr.error(this.translate.instant('LOGIN.TOASTR_ERROR_LOGGEDOUT_MESSAGE'), this.translate.instant('LOGIN.TOASTR_ERROR_LOGGEDOUT_H1'), { toastClass: 'ngx-toastr lobbyless-toast' });
            break;
          case "SYSTEM": //"network" type has a message to translate
            this.toastr.error(
              this.translate.instant(err.message ?? 'ERRORS.GENERAL_MESSAGE'),
              this.translate.instant('ACCOUNT.MESSAGES.ERROR_H1'),
              { toastClass: 'ngx-toastr lobbyless-toast' }
            );
            break;
          default:
            this.toastr.error(
              err.message ?? this.translate.instant('ERRORS.GENERAL_MESSAGE'),
              this.translate.instant('ACCOUNT.MESSAGES.ERROR_H1'),
              { toastClass: 'ngx-toastr lobbyless-toast' }
            );
            this.clearToastr(); //if the message is still there, will come in every minute - no action needed
            break;
        }

      }

    });
  }

  acknowledge(messageId: number): void {
    this.http.post<any>(`${this.configs.apiUrl}`, { "jsonrpc": "2.0", "id": this.configs.casinoId, "method": "user.message.acknowledge", "params": { messageId } }).pipe(
      map(response => {
        if ('result' in response && 'nextMessage' in response.result) {
          return response.result.nextMessage as SystemMessage | null;
        }
        throw response;
      }),
      catchError(error => throwError(() => this.errorMapper.mapAPIerrors(error)))
    ).subscribe({
      next: (nextMessage) => {
        this.currentMessageSignal.set(nextMessage);
        if (!nextMessage) {
          this.hasUnreadSignal.set(false);
          this.modalService.close('systemMessageModal');
        }
      },
      error: (err: AppErrorModel) => {
        console.error('Error acknowledging message:', err);

        switch (err.type) {
          case "SESSION":
            this.authService.resetUserSession();
            this.toastr.error(this.translate.instant('LOGIN.TOASTR_ERROR_LOGGEDOUT_MESSAGE'), this.translate.instant('LOGIN.TOASTR_ERROR_LOGGEDOUT_H1'), { toastClass: 'ngx-toastr lobbyless-toast' });
            break;
          case "SYSTEM": //"network" type has a message to translate
            this.toastr.error(
              this.translate.instant(err.message ?? 'ERRORS.GENERAL_MESSAGE'),
              this.translate.instant('ACCOUNT.MESSAGES.ERROR_H1'),
              { toastClass: 'ngx-toastr lobbyless-toast' }
            );
            break;
          default: //"USER" type with message & fallback for anything else (no "field" type)
            this.toastr.error(
              err.message ?? this.translate.instant('ERRORS.GENERAL_MESSAGE'),
              this.translate.instant('ERRORS.GENERAL_TITLE'),
              { toastClass: 'ngx-toastr lobbyless-toast' }
            );
        }

        this.modalService.close('systemMessageModal');
      }
    });
  }

  onModalClosedWithoutAck(): void {
    if (this.hasUnreadSignal() && this.currentMessageSignal()) {
      //remove message as we will get it again with readNext from toastr when clicked
      this.currentMessageSignal.set(null);
      //bring back new message toastr
      this.showUnreadToastr();
    }
  }

  private clearToastr(): void {
    if (this.activeToast) {
      this.toastr.clear(this.activeToast.toastId);
      this.activeToast = undefined;
    }
  }

  clearMessageState(): void {
    this.clearToastr();
    this.currentMessageSignal.set(null);
    this.hasUnreadSignal.set(false);
  }


}
